import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import axios from '../api/axios';

const FileUpload = ({ itemId, onUploadSuccess, userRole }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [visibleToHomeowner, setVisibleToHomeowner] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif',
                           'application/pdf', 'application/msword',
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           'application/vnd.ms-excel',
                           'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                           'text/plain', 'text/csv'];

      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Allowed: images, PDFs, documents, spreadsheets, text files');
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('visible_to_homeowner', visibleToHomeowner);

      const response = await axios.post(`/api/items/${itemId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSelectedFile(null);
      setVisibleToHomeowner(false);

      // Reset file input
      const fileInput = document.getElementById(`file-input-${itemId}`);
      if (fileInput) fileInput.value = '';

      if (onUploadSuccess) {
        onUploadSuccess(response.data.attachment);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Attach File
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
          disabled={uploading}
        >
          Choose File
          <input
            id={`file-input-${itemId}`}
            type="file"
            hidden
            onChange={handleFileSelect}
            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          />
        </Button>

        {selectedFile && (
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </Typography>
        )}

        {selectedFile && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </Box>

      {userRole === 'contractor' && (
        <FormControlLabel
          control={
            <Checkbox
              checked={visibleToHomeowner}
              onChange={(e) => setVisibleToHomeowner(e.target.checked)}
              disabled={uploading}
            />
          }
          label="Visible to homeowner"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};

export default FileUpload;
